// src/pages/documentation/quick-start/QuickStartIndex.jsx

import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faRocket,
    faUser,
    faSignInAlt,
    faStore,
    faArrowRight,
    faCheckCircle,
    faClock
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

const QuickStartIndex = () => {
    const steps = [
        {
            id: 'registro',
            title: 'Paso 1: Crear tu cuenta',
            description: 'Regístrate en PetitPrime con tu email y completa tus datos personales',
            icon: faUser,
            path: '/documentation/quick-start/registro',
            color: 'blue',
            estimatedTime: '3 min',
            completed: false
        },
        {
            id: 'login',
            title: 'Paso 2: Iniciar sesión',
            description: 'Accede a tu cuenta después de verificar tu email',
            icon: faSignInAlt,
            path: '/documentation/quick-start/login',
            color: 'green',
            estimatedTime: '2 min',
            completed: false
        },
        {
            id: 'onboarding',
            title: 'Paso 3: Configurar tu negocio',
            description: 'Completa el proceso de onboarding para tener todo listo',
            icon: faStore,
            path: '/documentation/quick-start/onboarding',
            color: 'purple',
            estimatedTime: '10 min',
            completed: false
        }
    ];

    const colorClasses = {
        blue: {
            bg: 'bg-blue-100',
            text: 'text-blue-600',
            border: 'border-blue-200',
            button: 'bg-blue-600 hover:bg-blue-700'
        },
        green: {
            bg: 'bg-green-100',
            text: 'text-green-600',
            border: 'border-green-200',
            button: 'bg-green-600 hover:bg-green-700'
        },
        purple: {
            bg: 'bg-purple-100',
            text: 'text-purple-600',
            border: 'border-purple-200',
            button: 'bg-purple-600 hover:bg-purple-700'
        }
    };

    return (
        <DocumentationLayout>
            <div className="p-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                            <FontAwesomeIcon icon={faRocket} className="text-white text-2xl" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Inicio Rápido</h1>
                            <p className="text-gray-600">Guía paso a paso para empezar a usar PetitPrime</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Link to="/documentation" className="hover:text-gray-700">Centro de Ayuda</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <span className="text-gray-700">Inicio Rápido</span>
                    </div>
                </div>

                {/* Overview */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 mb-8">
                    <div className="flex items-start gap-4">
                        <FontAwesomeIcon icon={faRocket} className="text-green-600 text-xl mt-1" />
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-2">¿Qué vas a aprender?</h3>
                            <p className="text-gray-700 mb-3">
                                Esta guía te llevará paso a paso desde crear tu cuenta hasta tener tu sistema de fidelización funcionando. 
                                Sigue los 3 pasos en orden y en menos de 15 minutos estarás listo para empezar.
                            </p>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                                <span className="flex items-center gap-2">
                                    <FontAwesomeIcon icon={faClock} />
                                    Tiempo total estimado: 15 minutos
                                </span>
                                <span className="flex items-center gap-2">
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                    3 pasos principales
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Steps Grid */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                    {steps.map((step, index) => (
                        <div
                            key={step.id}
                            className={`bg-white border-2 ${colorClasses[step.color]?.border || 'border-gray-200'} rounded-xl p-6 hover:shadow-lg transition-all`}
                        >
                            <div className={`inline-flex p-3 ${colorClasses[step.color]?.bg || 'bg-gray-100'} rounded-lg mb-4`}>
                                <FontAwesomeIcon
                                    icon={step.icon}
                                    className={`${colorClasses[step.color]?.text || 'text-gray-600'} text-2xl`}
                                />
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                                <span className={`text-xs font-bold ${colorClasses[step.color]?.text || 'text-gray-600'} bg-gray-100 px-2 py-1 rounded`}>
                                    PASO {index + 1}
                                </span>
                                {step.completed && (
                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded flex items-center gap-1">
                                        <FontAwesomeIcon icon={faCheckCircle} className="text-xs" />
                                        Completado
                                    </span>
                                )}
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">{step.title}</h3>
                            <p className="text-gray-600 text-sm mb-4">{step.description}</p>
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                    <FontAwesomeIcon icon={faClock} />
                                    {step.estimatedTime}
                                </span>
                                <Link
                                    to={step.path}
                                    className={`inline-flex items-center gap-2 ${colorClasses[step.color]?.button || 'bg-gray-600'} text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium`}
                                >
                                    Comenzar
                                    <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Quick Start Button */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-8">
                    <div className="text-center">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">¿Listo para empezar?</h3>
                        <p className="text-gray-700 mb-4">
                            Comienza con el primer paso: crear tu cuenta en PetitPrime
                        </p>
                        <Link
                            to="/documentation/quick-start/registro"
                            className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium text-lg"
                        >
                            <FontAwesomeIcon icon={faRocket} />
                            Comenzar con el Paso 1
                        </Link>
                    </div>
                </div>

                {/* Support */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">¿Necesitas ayuda?</h3>
                    <p className="text-gray-600 mb-4">
                        Si tienes alguna duda durante el proceso, nuestro equipo de soporte está aquí para ayudarte.
                    </p>
                    <a
                        href="mailto:soporte@petitprime.com"
                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                    >
                        <FontAwesomeIcon icon={faRocket} />
                        Contactar Soporte
                    </a>
                </div>
            </div>
        </DocumentationLayout>
    );
};

export default QuickStartIndex;

